import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'

/** Hur länge vi väntar in portalens bilder innan utskriften startar ändå. En
 *  trasig eller långsam figur ska försena utskriften, aldrig hindra den. */
const BILDVANTAN_MS = 3000

/**
 * Ser till att portalens bilder finns på plats innan utskriften startar.
 *
 * Illustrationer laddas med `loading="lazy"`, vilket är rätt på en kapitelsida
 * med många figurer. Men portalen är `display: none` på skärmen, och en lat
 * bild utan layoutbox hämtas aldrig — den låg kvar med naturalWidth 0 långt
 * efter att utskriften startat, så figuren saknades på papperet. Här tvingas de
 * att hämtas och väntas in.
 */
const forberedBilder = (rot: HTMLElement | null) => {
  const bilder = rot ? Array.from(rot.querySelectorAll('img')) : []
  if (bilder.length === 0) return Promise.resolve()

  const laddade = bilder.map((bild) => {
    bild.loading = 'eager'
    if (bild.complete && bild.naturalWidth > 0) return Promise.resolve()
    return new Promise<void>((klar) => {
      bild.addEventListener('load', () => klar(), { once: true })
      bild.addEventListener('error', () => klar(), { once: true })
    })
  })

  return Promise.race([
    Promise.all(laddade).then(() => undefined),
    new Promise<void>((klar) => setTimeout(klar, BILDVANTAN_MS)),
  ])
}

/** Den för tillfället riggade utskriftens avslut. Bara en utskrift får vara
 *  riggad åt gången — annars ligger två portaler kvar i DOM och skrivs ut
 *  tillsammans när läraren väljer nästa material. */
let stangAktiv: (() => void) | null = null

/** Antal monterade portaler. Body-klassen tas bort först när den sista är borta:
 *  när en ny utskrift ersätter en gammal monteras den nya innan den gamla
 *  städas bort, och en blind remove i den gamlas städning skulle då slå bort
 *  avgränsningen för den nya. */
let antalPortaler = 0

/**
 * Tillstånd för en utskriftsknapp.
 *
 * `skrivUt` skriver ut på nytt direkt om utskriften redan är riggad, i stället
 * för att förlita sig på att tillståndet hunnit nollställas. Det är det som gör
 * att knappen inte kan gå död: alla webbläsare berättar inte när en utskrift är
 * avslutad, och tidigare krävdes ett sådant besked innan knappen svarade igen.
 */
export function useUtskrift() {
  const [aktiv, setAktiv] = useState(false)
  return {
    aktiv,
    skrivUt: () => (aktiv ? window.print() : setAktiv(true)),
    avsluta: () => setAktiv(false),
  }
}

/** Skriver ut ENBART sitt innehåll, utan webbkrom: innehållet renderas i en
 *  portal direkt under <body>, en body-klass gömmer resten av appen i
 *  utskriften, och utskriftsdialogen öppnas automatiskt. Används för arbetsblad,
 *  prov och elevmaterial.
 *
 *  Avgränsningen rivs BARA av en äkta signal om att utskriften är klar:
 *  `afterprint`, eller att mediet går från print tillbaka till skärm (för
 *  webbläsare som saknar afterprint). Den får aldrig rivas av en gissning som
 *  fönsterfokus eller en tidsgräns — webbläsaren fångar sidan när den är redo,
 *  och rivs portalen dessförinnan skrivs hela sidan ut i stället för materialet.
 *  Att knappen ska gå att trycka på igen löses av useUtskrift, inte här. */
export default function UtskriftPortal({ children, onKlar }: { children: ReactNode; onKlar: () => void }) {
  // onKlar är oftast en ny pilfunktion vid varje render. Den läses via en ref så
  // att effekten kör exakt en gång — annars skulle en omrendering mitt i
  // utskriften kunna utlösa print() en gång till.
  const onKlarRef = useRef(onKlar)
  onKlarRef.current = onKlar
  const mal = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let avslutad = false
    const avsluta = () => {
      if (avslutad) return
      avslutad = true
      onKlarRef.current()
    }
    const vidMediaByte = (e: MediaQueryListEvent) => {
      if (!e.matches) avsluta()
    }

    stangAktiv?.()
    stangAktiv = avsluta

    const printMedia = window.matchMedia('print')
    antalPortaler++
    document.body.classList.add('print-fokus')
    window.addEventListener('afterprint', avsluta)
    printMedia.addEventListener('change', vidMediaByte)

    // Bilderna väntas in före print(). Har portalen hunnit stängas under tiden
    // ska ingen utskrift starta.
    const timer = setTimeout(() => {
      forberedBilder(mal.current).then(() => {
        if (!avslutad) window.print()
      })
    }, 80)

    return () => {
      clearTimeout(timer)
      window.removeEventListener('afterprint', avsluta)
      printMedia.removeEventListener('change', vidMediaByte)
      antalPortaler--
      if (antalPortaler === 0) document.body.classList.remove('print-fokus')
      if (stangAktiv === avsluta) stangAktiv = null
    }
  }, [])

  return createPortal(
    <div className="print-fokus-mal dokument" ref={mal}>
      {children}
    </div>,
    document.body,
  )
}
